// src/components/resources/glossary/FormulaCallout.tsx

import type { FormulaCallout as FormulaCalloutData } from '~/lib/resources/types/glossary/terms'

interface FormulaCalloutProps {
  callout: FormulaCalloutData
  className?: string
}

/**
 * Compact callout that surfaces a glossary term's `formulaCallout`
 * field — a one-line expression plus an optional plain-English
 * restatement. Rendered ABOVE the full `MetricsCalculation` block on
 * the calculation tab so a quick visual scan picks up the headline
 * formula before the per-component breakdown.
 *
 * The field carries authored prose that's pre-validated against the
 * `FORMULA_RESULT_UNITS` vocabulary by `scripts/process-glossary.ts` —
 * unlike `MetricCalculation.formula`, this expression is NOT tokenised
 * into component chips at render time, so it preserves natural
 * comparison operators (`<`, `≤`) and free-text terms (`p-value`,
 * "Treatment Conversions") verbatim. That makes it the right surface
 * for formulas that don't fit the canonical "X = A / B" shape.
 *
 * Server-renderable; no client interactivity. Pairs with the matching
 * field in the .md exporter so the rendered HTML and the static .md
 * source stay in lock-step.
 */
export function FormulaCallout({ callout, className = '' }: FormulaCalloutProps): JSX.Element {
  return (
    <aside
      className={`rounded-xl border border-indigo-200 bg-indigo-50 p-5 dark:border-indigo-900/60 dark:bg-indigo-950/30 ${className}`}
      aria-label="Formula callout"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
        Formula
      </p>
      <p className="mt-2 font-mono text-lg leading-snug text-gray-900 dark:text-white">
        {callout.expression}
        {callout.resultUnit ? (
          <span className="ml-2 align-middle text-xs font-normal uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
            ({callout.resultUnit})
          </span>
        ) : null}
      </p>
      {callout.plainEnglish ? (
        <p className="mt-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {callout.plainEnglish}
        </p>
      ) : null}
    </aside>
  )
}

export default FormulaCallout
